/**
 * Adapted from PlayCanvas Engine's scripts/esm/gsplat/gsplat-lines.mjs at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */

import * as THREE from 'three'
import type { GSplatResourceLease } from '@/plugins/gsplat/GSplatHandleBinding'
import type { GSplatGraphicsAdapter } from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import { GSplatFormat } from '@/plugins/gsplat/lod/GSplatFormat'
import type {
  GaussianSplatHandle,
  GaussianSplatLoadRequest,
} from '@/plugins/gsplat/types/PluginTypes'
import { GSplatContainer } from '@/plugins/gsplat/unified/resource/GSplatContainer'
import { getLoggerManager } from '@/shared/logging'
import type { AssetId, Disposable } from '@/shared/types/core'

const log = getLoggerManager().getLogger('gsplat:lines')
const UP = new THREE.Vector3(0, 1, 0)
const RIGHT = new THREE.Vector3(1, 0, 0)

export interface GSplatVector3Input {
  readonly x: number
  readonly y: number
  readonly z: number
}

export interface GSplatColorInput {
  readonly r: number
  readonly g: number
  readonly b: number
  readonly a?: number
}

export interface GSplatLinesRuntime {
  readonly graphicsAdapter: GSplatGraphicsAdapter
  createWithResource(
    request: Readonly<GaussianSplatLoadRequest>,
    resource: GSplatResourceLease
  ): GaussianSplatHandle
  replaceWithResource(
    id: AssetId,
    resource: GSplatResourceLease,
    metadata?: { readonly url?: string; readonly name?: string }
  ): GaussianSplatHandle
  mount(handle: GaussianSplatHandle): void
  disposeAsset(id: AssetId): void
}

export interface GSplatLinesOptions {
  readonly id?: AssetId
  readonly name?: string
}

export type GSplatLinesErrorCode =
  | 'GSPLAT_LINES_DISPOSED'
  | 'GSPLAT_LINES_INVALID_POSITION'
  | 'GSPLAT_LINES_INVALID_COLOR'
  | 'GSPLAT_LINES_INVALID_THICKNESS'
  | 'GSPLAT_LINES_INVALID_HEAD_SIZE'
  | 'GSPLAT_LINES_INVALID_ARROW_LENGTH'
  | 'GSPLAT_LINES_CAPACITY_OVERFLOW'
  | 'GSPLAT_LINES_ALLOCATION_FAILED'
  | 'GSPLAT_LINES_FILL_FAILED'
  | 'GSPLAT_LINES_UPLOAD_FAILED'
  | 'GSPLAT_LINES_UPDATE_FAILED'
  | 'GSPLAT_LINES_COMMIT_FAILED'
  | 'GSPLAT_LINES_STALE_SNAPSHOT'

export class GSplatLinesError extends Error {
  constructor(
    readonly code: GSplatLinesErrorCode,
    message: string,
    readonly cause?: unknown
  ) {
    super(message)
    this.name = 'GSplatLinesError'
  }
}

interface GSplatLineColor {
  readonly r: number
  readonly g: number
  readonly b: number
  readonly a: number
}

interface GSplatLineSegment {
  readonly start: THREE.Vector3
  readonly end: THREE.Vector3
  readonly startColor: GSplatLineColor
  readonly endColor: GSplatLineColor
  readonly thickness: number
}

interface GSplatLinesPrimitive {
  readonly lines: readonly GSplatLineSegment[]
  readonly splatCount: number
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

/** 冻结上游密度公式；调用方负责先验证 thickness。 */
export function calculateSplatCount(
  start: GSplatVector3Input,
  end: GSplatVector3Input,
  thickness: number
): number {
  const dx = end.x - start.x
  const dy = end.y - start.y
  const dz = end.z - start.z
  const length = Math.sqrt(dx * dx + dy * dy + dz * dz)
  return Math.max(1, Math.ceil(length / (thickness * 0.5)))
}

function clonePosition(value: GSplatVector3Input, field: string): THREE.Vector3 {
  if (![value.x, value.y, value.z].every(Number.isFinite)) {
    throw new GSplatLinesError(
      'GSPLAT_LINES_INVALID_POSITION',
      `GSplatLines ${field} 必须是有限三维坐标`
    )
  }
  return new THREE.Vector3(value.x, value.y, value.z)
}

function cloneColor(value: GSplatColorInput, field: string): GSplatLineColor {
  const alpha = value.a ?? 1
  if (![value.r, value.g, value.b, alpha].every(Number.isFinite)) {
    throw new GSplatLinesError(
      'GSPLAT_LINES_INVALID_COLOR',
      `GSplatLines ${field} 必须是有限 RGBA 颜色`
    )
  }
  return Object.freeze({ r: value.r, g: value.g, b: value.b, a: alpha })
}

function validateThickness(thickness: number): void {
  if (!Number.isFinite(thickness) || thickness <= 0) {
    throw new GSplatLinesError(
      'GSPLAT_LINES_INVALID_THICKNESS',
      `GSplatLines thickness 必须是有限正数: ${thickness}`
    )
  }
}

function freezeLine(input: GSplatLineSegment): GSplatLineSegment {
  return Object.freeze(input)
}

/** FrameDriver Update phase 驱动的 backend-neutral debug primitive generator。 */
export class GSplatLines implements Disposable {
  private readonly id?: AssetId
  private readonly name: string
  private readonly primitives = new Map<number, GSplatLinesPrimitive>()
  private nextHandle = 1
  private revision = 0
  private committedRevision = 0
  private formatValue: GSplatFormat | null = null
  private handleValue: GaussianSplatHandle | null = null
  private stagedHandle: GaussianSplatHandle | null = null
  private stagedContainer: GSplatContainer | null = null
  private generation = 0
  private initialized = false
  private disposed = false
  private errorValue: GSplatLinesError | null = null

  constructor(
    private readonly runtime: GSplatLinesRuntime,
    options: GSplatLinesOptions = {},
    private readonly onDispose?: (generator: GSplatLines) => void
  ) {
    this.id = options.id
    this.name = options.name ?? 'GSplatLines'
  }

  get handle(): GaussianSplatHandle | null {
    return this.handleValue
  }

  get format(): GSplatFormat | null {
    return this.formatValue
  }

  get lastError(): GSplatLinesError | null {
    return this.errorValue
  }

  get primitiveCount(): number {
    return this.primitives.size
  }

  initialize(): void {
    this.assertActive()
    if (this.initialized) return
    this.initialized = true
  }

  addLine(
    start: GSplatVector3Input,
    end: GSplatVector3Input,
    startColor: GSplatColorInput,
    endColor: GSplatColorInput,
    thickness: number
  ): number {
    this.assertActive()
    validateThickness(thickness)
    const line = freezeLine({
      start: clonePosition(start, 'start'),
      end: clonePosition(end, 'end'),
      startColor: cloneColor(startColor, 'startColor'),
      endColor: cloneColor(endColor, 'endColor'),
      thickness,
    })
    const splatCount = this.validateSplatCount(calculateSplatCount(line.start, line.end, thickness))
    return this.addPrimitive(Object.freeze({ lines: Object.freeze([line]), splatCount }))
  }

  addLineSimple(
    start: GSplatVector3Input,
    end: GSplatVector3Input,
    color: GSplatColorInput,
    thickness: number
  ): number {
    return this.addLine(start, end, color, color, thickness)
  }

  addArrow(
    startInput: GSplatVector3Input,
    endInput: GSplatVector3Input,
    colorInput: GSplatColorInput,
    thickness: number,
    headSizeInput?: number
  ): number {
    this.assertActive()
    validateThickness(thickness)
    const start = clonePosition(startInput, 'start')
    const end = clonePosition(endInput, 'end')
    const color = cloneColor(colorInput, 'color')
    const headSize = headSizeInput ?? thickness * 9
    if (!Number.isFinite(headSize) || headSize < 0) {
      throw new GSplatLinesError(
        'GSPLAT_LINES_INVALID_HEAD_SIZE',
        `GSplatLines headSize 必须是有限非负数: ${headSize}`
      )
    }

    const direction = new THREE.Vector3().subVectors(end, start)
    const length = direction.length()
    if (!Number.isFinite(length) || length === 0) {
      throw new GSplatLinesError(
        'GSPLAT_LINES_INVALID_ARROW_LENGTH',
        'GSplatLines arrow 起点与终点不能重合'
      )
    }
    direction.multiplyScalar(1 / length)

    const perpX = new THREE.Vector3()
    if (Math.abs(direction.y) < 0.9) {
      perpX.crossVectors(direction, UP)
    } else {
      perpX.crossVectors(direction, RIGHT)
    }
    perpX.normalize()
    const perpY = new THREE.Vector3().crossVectors(direction, perpX).normalize()
    const lineRatio = Math.max(0, (length - headSize) / length)
    const mainEnd = new THREE.Vector3().lerpVectors(start, end, lineRatio)
    const headBase = new THREE.Vector3().lerpVectors(start, end, lineRatio)
    const wingOffset = headSize * 0.4
    const corners = [
      headBase.clone().add(perpX.clone().multiplyScalar(wingOffset)),
      headBase.clone().sub(perpX.clone().multiplyScalar(wingOffset)),
      headBase.clone().add(perpY.clone().multiplyScalar(wingOffset)),
      headBase.clone().sub(perpY.clone().multiplyScalar(wingOffset)),
    ]
    const lines = [
      this.createLine(start, mainEnd, color, color, thickness),
      ...corners.map((corner) => this.createLine(corner, end, color, color, thickness)),
    ]
    return this.addPrimitive(this.createPrimitive(lines))
  }

  addAABB(
    minInput: GSplatVector3Input,
    maxInput: GSplatVector3Input,
    colorInput: GSplatColorInput,
    thickness: number
  ): number {
    this.assertActive()
    validateThickness(thickness)
    const min = clonePosition(minInput, 'min')
    const max = clonePosition(maxInput, 'max')
    const color = cloneColor(colorInput, 'color')
    const corners = [
      new THREE.Vector3(min.x, min.y, min.z),
      new THREE.Vector3(max.x, min.y, min.z),
      new THREE.Vector3(max.x, max.y, min.z),
      new THREE.Vector3(min.x, max.y, min.z),
      new THREE.Vector3(min.x, min.y, max.z),
      new THREE.Vector3(max.x, min.y, max.z),
      new THREE.Vector3(max.x, max.y, max.z),
      new THREE.Vector3(min.x, max.y, max.z),
    ]
    const edges = [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 0],
      [4, 5],
      [5, 6],
      [6, 7],
      [7, 4],
      [0, 4],
      [1, 5],
      [2, 6],
      [3, 7],
    ] as const
    const lines = edges.map(([startIndex, endIndex]) =>
      this.createLine(corners[startIndex], corners[endIndex], color, color, thickness)
    )
    return this.addPrimitive(this.createPrimitive(lines))
  }

  removePrimitive(handle: number): boolean {
    this.assertActive()
    const removed = this.primitives.delete(handle)
    if (removed) this.revision++
    return removed
  }

  clear(): void {
    this.assertActive()
    if (this.primitives.size === 0) return
    this.primitives.clear()
    this.revision++
  }

  postUpdate(): void {
    this.assertActive()
    if (!this.initialized) this.initialize()
    if (this.revision === this.committedRevision) return
    const snapshotRevision = this.revision
    const snapshot = Object.freeze([...this.primitives.values()])
    try {
      this.rebuild(snapshot, snapshotRevision)
      this.assertSnapshot(snapshotRevision)
      this.committedRevision = snapshotRevision
      this.errorValue = null
    } catch (error) {
      const structured = this.toError(error, 'GSPLAT_LINES_COMMIT_FAILED', 'primitive 重建失败')
      this.errorValue = structured
      log.warn(`GSplatLines 重建失败: code=${structured.code}, reason=${structured.message}`)
    }
  }

  destroy(): void {
    if (this.disposed) return
    this.disposed = true
    this.generation++
    this.primitives.clear()
    this.revision++
    this.stagedContainer?.destroy()
    this.stagedContainer = null
    const handle = this.handleValue
    const stagedHandle = this.stagedHandle
    this.handleValue = null
    this.stagedHandle = null
    let firstError: unknown
    const disposeHandle = (target: GaussianSplatHandle | null) => {
      if (!target) return
      try {
        this.runtime.disposeAsset(target.id)
      } catch (error) {
        firstError ??= error
      }
    }
    disposeHandle(handle)
    if (stagedHandle !== handle) disposeHandle(stagedHandle)
    try {
      this.onDispose?.(this)
    } catch (error) {
      firstError ??= error
    }
    if (firstError) throw firstError
  }

  dispose(): void {
    this.destroy()
  }

  private rebuild(snapshot: readonly GSplatLinesPrimitive[], snapshotRevision: number): void {
    const operationGeneration = ++this.generation
    let totalSplats = 0
    for (const primitive of snapshot) {
      totalSplats += primitive.splatCount
      if (!Number.isSafeInteger(totalSplats)) {
        throw new GSplatLinesError(
          'GSPLAT_LINES_CAPACITY_OVERFLOW',
          'GSplatLines splat 数量超出安全整数范围'
        )
      }
    }
    this.assertSnapshot(snapshotRevision, operationGeneration)
    if (totalSplats === 0) {
      this.commitEmpty(snapshotRevision, operationGeneration)
      return
    }

    const adapter = this.runtime.graphicsAdapter
    let container: GSplatContainer | null = null
    try {
      try {
        const format = (this.formatValue ??= GSplatFormat.createSimpleFormat(adapter))
        container = new GSplatContainer(adapter, totalSplats, format)
      } catch (error) {
        throw new GSplatLinesError(
          'GSPLAT_LINES_ALLOCATION_FAILED',
          `GSplatLines container 分配失败: ${errorMessage(error)}`,
          error
        )
      }
      this.stagedContainer = container
      this.assertSnapshot(snapshotRevision, operationGeneration)

      const texelCount = container.textureDimensions.width * container.textureDimensions.height
      const centerData = new Float32Array(texelCount * 4)
      const colorData = new Uint16Array(texelCount * 4)
      const centers = container.centers
      let minX = Number.POSITIVE_INFINITY
      let minY = Number.POSITIVE_INFINITY
      let minZ = Number.POSITIVE_INFINITY
      let maxX = Number.NEGATIVE_INFINITY
      let maxY = Number.NEGATIVE_INFINITY
      let maxZ = Number.NEGATIVE_INFINITY
      let maxThickness = 0
      let splatIndex = 0
      try {
        for (const primitive of snapshot) {
          for (const line of primitive.lines) {
            const numSplats = calculateSplatCount(line.start, line.end, line.thickness)
            maxThickness = Math.max(maxThickness, line.thickness)
            for (let index = 0; index < numSplats; index++) {
              const t = numSplats > 1 ? index / (numSplats - 1) : 0.5
              const x = line.start.x + (line.end.x - line.start.x) * t
              const y = line.start.y + (line.end.y - line.start.y) * t
              const z = line.start.z + (line.end.z - line.start.z) * t
              const r = line.startColor.r + (line.endColor.r - line.startColor.r) * t
              const g = line.startColor.g + (line.endColor.g - line.startColor.g) * t
              const b = line.startColor.b + (line.endColor.b - line.startColor.b) * t
              const a = line.startColor.a + (line.endColor.a - line.startColor.a) * t
              const centerOffset = splatIndex * 4
              centerData[centerOffset] = x
              centerData[centerOffset + 1] = y
              centerData[centerOffset + 2] = z
              centerData[centerOffset + 3] = line.thickness
              colorData[centerOffset] = THREE.DataUtils.toHalfFloat(r)
              colorData[centerOffset + 1] = THREE.DataUtils.toHalfFloat(g)
              colorData[centerOffset + 2] = THREE.DataUtils.toHalfFloat(b)
              colorData[centerOffset + 3] = THREE.DataUtils.toHalfFloat(a)
              const sortingOffset = splatIndex * 3
              centers[sortingOffset] = x
              centers[sortingOffset + 1] = y
              centers[sortingOffset + 2] = z
              minX = Math.min(minX, x)
              minY = Math.min(minY, y)
              minZ = Math.min(minZ, z)
              maxX = Math.max(maxX, x)
              maxY = Math.max(maxY, y)
              maxZ = Math.max(maxZ, z)
              splatIndex++
            }
          }
        }
      } catch (error) {
        throw new GSplatLinesError(
          'GSPLAT_LINES_FILL_FAILED',
          `GSplatLines stream 填充失败: ${errorMessage(error)}`,
          error
        )
      }
      if (splatIndex !== totalSplats) {
        throw new GSplatLinesError(
          'GSPLAT_LINES_FILL_FAILED',
          `GSplatLines splat 数量漂移: actual=${splatIndex}, expected=${totalSplats}`
        )
      }

      const centerTexture = container.getTexture('dataCenter')
      const colorTexture = container.getTexture('dataColor')
      if (!centerTexture || !colorTexture) {
        throw new GSplatLinesError(
          'GSPLAT_LINES_ALLOCATION_FAILED',
          'GSplatLines simple format texture 不完整'
        )
      }
      try {
        adapter.updateTexture(centerTexture, centerData)
        adapter.updateTexture(colorTexture, colorData)
      } catch (error) {
        throw new GSplatLinesError(
          'GSPLAT_LINES_UPLOAD_FAILED',
          `GSplatLines texture 上传失败: ${errorMessage(error)}`,
          error
        )
      }
      this.assertSnapshot(snapshotRevision, operationGeneration)

      try {
        container.aabb.set(
          new THREE.Vector3(minX - maxThickness, minY - maxThickness, minZ - maxThickness),
          new THREE.Vector3(maxX + maxThickness, maxY + maxThickness, maxZ + maxThickness)
        )
        container.update(totalSplats)
      } catch (error) {
        throw new GSplatLinesError(
          'GSPLAT_LINES_UPDATE_FAILED',
          `GSplatLines container update 失败: ${errorMessage(error)}`,
          error
        )
      }
      this.assertSnapshot(snapshotRevision, operationGeneration)

      const lease = container.acquire()
      container.destroy()
      container = null
      this.stagedContainer = null
      this.commitResource(snapshotRevision, operationGeneration, lease)
    } finally {
      if (this.stagedContainer === container) this.stagedContainer = null
      container?.destroy()
    }
  }

  private commitEmpty(snapshotRevision: number, operationGeneration: number): void {
    this.assertSnapshot(snapshotRevision, operationGeneration)
    if (!this.handleValue) return
    const adapter = this.runtime.graphicsAdapter
    let container: GSplatContainer | null = null
    try {
      try {
        const format = (this.formatValue ??= GSplatFormat.createSimpleFormat(adapter))
        container = new GSplatContainer(adapter, 0, format)
      } catch (error) {
        throw new GSplatLinesError(
          'GSPLAT_LINES_ALLOCATION_FAILED',
          `GSplatLines empty container 分配失败: ${errorMessage(error)}`,
          error
        )
      }
      this.stagedContainer = container
      this.assertSnapshot(snapshotRevision, operationGeneration)
      try {
        container.aabb.makeEmpty()
        container.update(0)
      } catch (error) {
        throw new GSplatLinesError(
          'GSPLAT_LINES_UPDATE_FAILED',
          `GSplatLines empty container update 失败: ${errorMessage(error)}`,
          error
        )
      }
      this.assertSnapshot(snapshotRevision, operationGeneration)
      const lease = container.acquire()
      container.destroy()
      container = null
      this.stagedContainer = null
      this.commitResource(snapshotRevision, operationGeneration, lease)
    } finally {
      if (this.stagedContainer === container) this.stagedContainer = null
      container?.destroy()
    }
  }

  private commitResource(
    snapshotRevision: number,
    operationGeneration: number,
    resource: GSplatResourceLease
  ): void {
    let lease: GSplatResourceLease | null = resource
    try {
      this.assertSnapshot(snapshotRevision, operationGeneration)
      let handle: GaussianSplatHandle
      if (this.handleValue) {
        handle = this.runtime.replaceWithResource(this.handleValue.id, lease, { name: this.name })
        lease = null
      } else {
        handle = this.runtime.createWithResource(
          Object.freeze({
            url: `procedural://gsplat-lines/${this.id ?? 'generated'}`,
            name: this.name,
            ...(this.id ? { id: this.id } : {}),
          }),
          lease
        )
        lease = null
        this.stagedHandle = handle
        try {
          this.runtime.mount(handle)
        } catch (error) {
          try {
            this.runtime.disposeAsset(handle.id)
          } catch (cleanupError) {
            log.warn(`GSplatLines mount 回滚失败: ${errorMessage(cleanupError)}`)
          } finally {
            this.stagedHandle = null
          }
          throw error
        }
      }
      this.assertSnapshot(snapshotRevision, operationGeneration)
      this.handleValue = handle
      this.stagedHandle = null
    } catch (error) {
      lease?.dispose()
      const stagedHandle = this.stagedHandle
      this.stagedHandle = null
      if (stagedHandle && stagedHandle !== this.handleValue) {
        try {
          this.runtime.disposeAsset(stagedHandle.id)
        } catch (cleanupError) {
          log.warn(`GSplatLines staged Handle 回滚失败: ${errorMessage(cleanupError)}`)
        }
      }
      throw this.toError(error, 'GSPLAT_LINES_COMMIT_FAILED', '提交 GSplat Resource 失败')
    }
  }

  private createLine(
    start: THREE.Vector3,
    end: THREE.Vector3,
    startColor: GSplatLineColor,
    endColor: GSplatLineColor,
    thickness: number
  ): GSplatLineSegment {
    return freezeLine({ start, end, startColor, endColor, thickness })
  }

  private createPrimitive(lines: readonly GSplatLineSegment[]): GSplatLinesPrimitive {
    let splatCount = 0
    for (const line of lines) {
      splatCount += this.validateSplatCount(
        calculateSplatCount(line.start, line.end, line.thickness)
      )
      if (!Number.isSafeInteger(splatCount)) {
        throw new GSplatLinesError(
          'GSPLAT_LINES_CAPACITY_OVERFLOW',
          'GSplatLines primitive splat 数量超出安全整数范围'
        )
      }
    }
    return Object.freeze({ lines: Object.freeze([...lines]), splatCount })
  }

  private addPrimitive(primitive: GSplatLinesPrimitive): number {
    if (!Number.isSafeInteger(this.nextHandle)) {
      throw new GSplatLinesError(
        'GSPLAT_LINES_CAPACITY_OVERFLOW',
        'GSplatLines primitive handle 已耗尽'
      )
    }
    const handle = this.nextHandle++
    this.primitives.set(handle, primitive)
    this.revision++
    return handle
  }

  private validateSplatCount(count: number): number {
    if (!Number.isSafeInteger(count) || count < 1) {
      throw new GSplatLinesError(
        'GSPLAT_LINES_CAPACITY_OVERFLOW',
        `GSplatLines splat count 非法: ${count}`
      )
    }
    return count
  }

  private assertSnapshot(snapshotRevision: number, operationGeneration = this.generation): void {
    if (
      this.disposed ||
      this.generation !== operationGeneration ||
      this.revision !== snapshotRevision
    ) {
      throw new GSplatLinesError(
        'GSPLAT_LINES_STALE_SNAPSHOT',
        'GSplatLines primitive snapshot 已失效'
      )
    }
  }

  private assertActive(): void {
    if (this.disposed) {
      throw new GSplatLinesError('GSPLAT_LINES_DISPOSED', 'GSplatLines 已销毁')
    }
  }

  private toError(error: unknown, code: GSplatLinesErrorCode, prefix: string): GSplatLinesError {
    if (error instanceof GSplatLinesError) return error
    return new GSplatLinesError(code, `${prefix}: ${errorMessage(error)}`, error)
  }
}
